import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Legend } from "recharts";
import { fmtMs, apiName, PRINT_NAMES } from "./series";

const AX = { stroke: "#71717a", fontSize: 11, tickLine: false, axisLine: false };
const GRID = { stroke: "rgba(255,255,255,0.06)", vertical: false };
const TIP = { contentStyle: { background: "#18181b", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, fontSize: 12, color: "#e4e4e7" }, labelStyle: { color: "#a1a1aa" }, cursor: { fill: "rgba(255,255,255,0.04)" } };
const TEAL = "#2dd4bf", TEAL_DIM = "rgba(45,212,191,0.35)", AMBER = "#fbbf24", ROSE = "#fb7185", ZINC = "#52525b", WHITE = "#f4f4f5";

export function Panel({ title, sub, children, testId, right, scope }) {
  return (
    <section className="rounded-2xl bg-zinc-900/70 ring-1 ring-white/[0.06] p-4 sm:p-5 backdrop-blur" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0"><h3 className="text-sm font-semibold tracking-wide text-zinc-100 flex items-center gap-2 flex-wrap">{title}{scope}</h3>{sub && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}</div>
        {right}
      </div>
      {children}
    </section>
  );
}

// Scope pill: FULL PERIOD (server summary over the whole window) vs LATEST N EVENTS (client-side, may be partial).
export function Scope({ full, hours, scope, testId }) {
  if (full) return <span className="rounded-md px-1.5 py-0.5 text-[10px] font-medium tracking-wide ring-1 ring-teal-400/30 text-teal-200 bg-teal-400/10" data-testid={testId}>WHOLE PERIOD · {hours} h</span>;
  const partial = scope?.partial;
  return <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium tracking-wide ring-1 ${partial ? "ring-amber-300/40 text-amber-200 bg-amber-300/10" : "ring-white/10 text-zinc-300 bg-white/[0.03]"}`} data-testid={testId}>LATEST {scope?.loaded ?? 0} EVENTS{partial ? " · PARTIAL" : ""}</span>;
}

export const Empty = ({ text = "No samples in this window" }) => <div className="h-[180px] flex items-center justify-center text-xs text-zinc-500" data-testid="chart-empty">{text}</div>;

export function TimingChart({ data }) {
  if (!data.some((d) => d.checkoutN || d.newSaleN)) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid {...GRID} /><XAxis dataKey="label" {...AX} /><YAxis {...AX} tickFormatter={fmtMs} width={66} />
        <Tooltip {...TIP} formatter={(v, n) => [fmtMs(v), n]} />
        <Bar dataKey="checkoutP95" name="Checkout p95" fill={TEAL_DIM} radius={[4, 4, 0, 0]} />
        <Line type="monotone" dataKey="checkoutP50" name="Checkout p50" stroke={TEAL} strokeWidth={2} dot={false} connectNulls />
        <Line type="monotone" dataKey="newSaleP95" name="New Sale ready p95" stroke={WHITE} strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 3" />
        <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function LongTaskChart({ data }) {
  if (!data.some((d) => d.longTasks || d.suspension || d.foreground || d.minor)) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
        <CartesianGrid {...GRID} /><XAxis dataKey="label" {...AX} /><YAxis {...AX} allowDecimals={false} />
        <Tooltip {...TIP} />
        <Bar dataKey="longTasks" name="Long tasks (>50 ms)" fill={TEAL_DIM} stackId="a" radius={[0, 0, 0, 0]} />
        <Bar dataKey="foreground" name="Foreground blocks" fill={ROSE} stackId="b" />
        <Bar dataKey="suspension" name="Timer suspension (not a freeze)" fill={ZINC} stackId="b" />
        <Bar dataKey="minor" name="Minor delays" fill={AMBER} stackId="b" radius={[4, 4, 0, 0]} />
        <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function ApiChart({ rows }) {
  if (!rows.length) return <Empty />;
  const data = rows.slice(0, 12).map((r) => ({ ...r, short: apiName(r.key) }));
  return (
    <ResponsiveContainer width="100%" height={Math.max(160, 26 * data.length + 30)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
        <XAxis type="number" {...AX} tickFormatter={fmtMs} /><YAxis type="category" dataKey="short" {...AX} width={170} />
        <Tooltip {...TIP} labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.key || ""}`} formatter={(v, n, p) => [`${fmtMs(v)} (n=${p.payload.n})`, n === "p95" ? "p95 (slowest 1 in 20)" : "p50 (typical)"]} />
        <Bar dataKey="p95" name="p95" fill={TEAL} radius={[0, 4, 4, 0]} background={{ fill: "rgba(255,255,255,0.03)" }} />
        <Bar dataKey="p50" name="p50" fill={WHITE} radius={[0, 4, 4, 0]} barSize={4} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function PrintChart({ stages }) {
  if (!stages.length) return <Empty text="No print lifecycle events in this window" />;
  const order = ["open", "opened", "beforeprint", "afterprint", "closed", "blocked", "cleanup_bound"];
  const data = [...stages].sort((a, b) => order.indexOf(a.stage) - order.indexOf(b.stage)).map((d) => ({ ...d, label: PRINT_NAMES[d.stage] || d.stage }));
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
        <CartesianGrid {...GRID} /><XAxis dataKey="label" {...AX} interval={0} tick={{ fontSize: 10 }} /><YAxis {...AX} allowDecimals={false} />
        <Tooltip {...TIP} labelFormatter={(l, p) => `${l} (${p?.[0]?.payload?.stage || ""})`} />
        <Bar dataKey="count" name="events" fill={TEAL_DIM} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
